import { type FC, useEffect, useMemo } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import StopWatchGif from '@/assets/images/stop_watch.gif';
import { IconBrandStar, IconRightArrow } from '@/components/Icons';
import { Logo } from '@/components/Logo';
import { useCountdown } from '@/utils/hooks';
import { getTimerText } from '@/utils/timer';

export const InterviewNotStartedPage: FC<{ targetTime: string }> = ({
  targetTime,
}) => {
  const { t } = useTranslation(['interview', 'common']);
  const navigate = useNavigate();

  const durationSec = useMemo(() => {
    return dayjs(targetTime).diff(dayjs(), 'second');
  }, [targetTime]);

  const showTimer: boolean = durationSec < 1 * 60 * 60;

  const [count, { startCountdown }] = useCountdown({
    countStart: durationSec,
  });

  const timerText = getTimerText(count);

  const currentYear = useMemo(() => {
    return dayjs().year();
  }, []);

  useEffect(() => {
    startCountdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Center flex="1" bg="gray.25">
      <Flex
        flexDirection="column"
        gap={0}
        w="460px"
        alignItems="center"
        bg="white"
        borderColor="gray.50"
        border={1}
        borderRadius={16}
        px={8}
        py={10}
        zIndex={1}
      >
        <Logo />

        <Box
          width="60px"
          height="60px"
          mt={4}
          mb={1.5}
          backgroundImage={StopWatchGif}
          bgSize="cover"
          bgPosition="center"
        />

        <VStack mb={3}>
          {showTimer ? (
            <Text
              color="gray.600"
              fontSize="md"
              fontWeight="normal"
              textAlign="center"
            >
              {t('interview:notStarted.desc')}
            </Text>
          ) : (
            <>
              <Text
                color="gray.600"
                fontSize="md"
                fontWeight="normal"
                textAlign="center"
              >
                {t('interview:notStarted.notStartedYet')}
              </Text>
              <Text
                color="gray.700"
                fontSize="md"
                fontWeight="normal"
                textAlign="center"
              >
                {`${t('interview:notStarted.willStartAt')} ${dayjs(targetTime).format('DD.MM.YYYY HH:mm')}`}
              </Text>
            </>
          )}
        </VStack>

        {showTimer && (
          <Text
            color="gray.900"
            fontSize="2xl"
            fontWeight="semibold"
            textAlign="center"
            mb={8}
          >
            {timerText} {t('common:timeAndDate.min')}
          </Text>
        )}

        <Button
          w="100%"
          variant="@primary"
          rightIcon={<IconRightArrow />}
          isDisabled={timerText !== '00:00'}
          onClick={() => navigate(0)}
        >
          {t('interview:notStarted.startInterview')}
        </Button>

        {timerText !== '00:00' && (
          <Button
            mt={3}
            rightIcon={<IconRightArrow fontSize="20px" />}
            onClick={() => navigate('/coach')}
            w="full"
          >
            {t('interview:tryInterviewCoach')}
          </Button>
        )}
      </Flex>

      <Text
        color="gray.600"
        fontWeight="normal"
        fontSize="sm"
        position="absolute"
        bottom={8}
        left="50%"
        transform="translateX(-50%)"
      >
        © Snaps.ai {currentYear}
      </Text>

      <Stack position="absolute" zIndex={0} height="100vh" width="100vw">
        <IconBrandStar
          position="absolute"
          top="4%"
          right="10%"
          fontSize="100px"
        />
        <IconBrandStar
          position="absolute"
          top="1%"
          left="10%"
          fontSize="248px"
        />
        <IconBrandStar
          position="absolute"
          bottom="6%"
          left="12%"
          fontSize="100px"
        />
        <IconBrandStar
          position="absolute"
          bottom="2%"
          right="6%"
          fontSize="314px"
        />
      </Stack>
    </Center>
  );
};
